// src/hooks/usePolledResource.ts
//
// Visibility-gated, non-overlapping polling of one resource, scoped to a key
// (plan 2026-10-07 campaign performance frontend, step 1; extracted from
// useLiveVisitors so the campaign live counts reuse the same rules).
//
// Rules this hook owns, so no consumer re-implements them:
//
//   - Polls on mount and every `intervalMs` while mounted AND the tab is
//     visible. A hidden tab stops the interval; becoming visible polls at once
//     and restarts it. Hidden at mount means no request at all.
//   - Never overlapping: a tick while a request is in flight is skipped.
//   - An answer is dropped unless the scope it was sent for and its in-flight
//     token are both still current. A `scopeKey` change (an account switch, a
//     different campaign) or an unmount therefore drops whatever is in flight.
//   - A `scopeKey` change resets the in-flight token and any backoff, and
//     polls the new scope at once. A null scope polls nothing.
//   - `backoffMs` may return a pause after an answer; no request goes out
//     until it has elapsed (ticks inside it are skipped).
//   - The fetcher and callbacks are held in refs and are NOT effect
//     dependencies: an inline arrow is a new function every render, and as a
//     dependency it would restart the effect, drop the in-flight token and
//     poll again on every render, breaking the no-overlap rule.

import { useEffect, useLayoutEffect, useRef, useState } from "react";

export type PollOutcome<T> =
  | { ok: true; value: T }
  | { ok: false; error: unknown };

export type UsePolledResourceOptions<T> = {
  /** The scope the data belongs to; null polls nothing. */
  scopeKey: string | null;
  fetch: () => Promise<T>;
  intervalMs: number;
  /**
   * An answer that counts as a failed poll: the previous data is kept and the
   * answer becomes `error`, exactly as if the request had thrown.
   */
  isFailure?: (value: T) => boolean;
  /** After a current answer: ms to wait before the next request, or null. */
  backoffMs?: (outcome: PollOutcome<T>) => number | null;
};

export type PolledResource<T> = {
  /**
   * "pending": no answer yet in this scope. "ok": the last poll answered.
   * "failed": the last poll threw, or answered with `isFailure`.
   */
  status: "pending" | "ok" | "failed";
  /** The last good answer in this scope, kept through a failed poll. */
  data: T | null;
  /** What the last failed poll threw (or the failing answer); null after "ok". */
  error: unknown;
};

type State<T> = PolledResource<T> & { scopeKey: string | null };

const PENDING: PolledResource<never> = {
  status: "pending",
  data: null,
  error: null,
};

export function usePolledResource<T>(
  options: UsePolledResourceOptions<T>,
): PolledResource<T> {
  const { scopeKey, intervalMs } = options;

  // The state carries the scope it belongs to; a state for another scope reads
  // as PENDING, so a scope switch never shows the previous scope's data.
  const [state, setState] = useState<State<T>>({ ...PENDING, scopeKey: null });

  const optionsRef = useRef(options);
  useLayoutEffect(() => {
    optionsRef.current = options;
  });

  const scopeRef = useRef<string | null>(scopeKey);
  const inFlightRef = useRef<symbol | null>(null);
  const pausedUntilRef = useRef<number | null>(null);

  useEffect(() => {
    scopeRef.current = scopeKey;
    inFlightRef.current = null;
    pausedUntilRef.current = null;
    if (!scopeKey) return;

    const poll = async () => {
      if (scopeRef.current !== scopeKey) return;
      if (document.hidden) return;
      if (inFlightRef.current) return;
      if (
        pausedUntilRef.current !== null &&
        Date.now() < pausedUntilRef.current
      ) {
        return;
      }

      const token = Symbol("polled-resource");
      inFlightRef.current = token;
      const isCurrent = () =>
        scopeRef.current === scopeKey && inFlightRef.current === token;

      let outcome: PollOutcome<T>;
      try {
        outcome = { ok: true, value: await optionsRef.current.fetch() };
      } catch (error) {
        outcome = { ok: false, error };
      }
      const current = isCurrent();
      if (inFlightRef.current === token) inFlightRef.current = null;
      if (!current) return;

      const { isFailure, backoffMs } = optionsRef.current;
      if (outcome.ok && isFailure?.(outcome.value)) {
        outcome = { ok: false, error: outcome.value };
      }
      const pause = backoffMs?.(outcome) ?? null;
      pausedUntilRef.current = pause === null ? null : Date.now() + pause;

      if (outcome.ok) {
        const value = outcome.value;
        setState({ status: "ok", data: value, error: null, scopeKey });
      } else {
        const error = outcome.error;
        setState((prev) => ({
          status: "failed",
          data: prev.scopeKey === scopeKey ? prev.data : null,
          error,
          scopeKey,
        }));
      }
    };

    let intervalId: number | null = null;
    const start = () => {
      if (intervalId === null) {
        intervalId = window.setInterval(() => void poll(), intervalMs);
      }
    };
    const stop = () => {
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        void poll();
        start();
      }
    };

    if (!document.hidden) {
      void poll();
      start();
    }
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      // Drops whatever is still in flight: its scope no longer matches.
      scopeRef.current = null;
      inFlightRef.current = null;
    };
  }, [scopeKey, intervalMs]);

  if (!scopeKey || state.scopeKey !== scopeKey) return PENDING;
  return { status: state.status, data: state.data, error: state.error };
}
